"use client";
import { useState } from "react";
import Head from "next/head";
import Link from "next/link";
import { showToast } from "@calcom/ui/components/toast";

const PERKS = [
  { emoji: "💰", title: "Set your own prices", desc: "Charge clients whatever you want. Keep 40–72% margins." },
  { emoji: "🎨", title: "100% white-label", desc: "Your domain, logo, colors, emails. Schedly is invisible." },
  { emoji: "🔌", title: "Full API access", desc: "Build, embed, or automate anything with our REST API." },
  { emoji: "📈", title: "Volume discounts", desc: "Wholesale rates drop as you scale — more clients, more margin." },
  { emoji: "🤖", title: "AI Voice Agent", desc: "Upsell AI phone booking at $0.08/min wholesale." },
  { emoji: "🎯", title: "Dedicated support", desc: "A named partner success contact + private partner Slack." },
];

export default function PartnerApplyPage() {
  const [formData, setFormData] = useState({
    companyName: "",
    contactName: "",
    contactEmail: "",
    website: "",
    partnerType: "RESELLER",
    description: "",
    estimatedClients: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await fetch("/api/partner/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          estimatedClients: formData.estimatedClients ? parseInt(formData.estimatedClients, 10) : 0,
        }),
      });
      if (r.ok) {
        setSubmitted(true);
      } else {
        const d = await r.json();
        showToast(d.error || "Failed to submit application", "error");
      }
    } catch {
      showToast("Failed to submit application", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "11px 14px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 10,
    fontSize: 14,
    color: "#111827",
    background: "#fff",
    outline: "none",
    transition: "border-color 0.15s, box-shadow 0.15s",
    fontFamily: "inherit",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 6,
    letterSpacing: "0.01em",
  };

  // ── SUCCESS STATE ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <>
        <Head>
          <title>Application Submitted — Schedly Partners</title>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />
        </Head>
        <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#fafbff 0%,#f0f4ff 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Inter,system-ui,sans-serif" }}>
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 24, padding: "52px 44px", maxWidth: 480, width: "100%", textAlign: "center", boxShadow: "0 20px 60px rgba(99,102,241,0.1)" }}>
            <div style={{ width: 72, height: 72, borderRadius: 22, background: "linear-gradient(135deg,#6366f1,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", boxShadow: "0 12px 32px rgba(99,102,241,0.35)" }}>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <h1 style={{ color: "#111827", fontSize: 26, fontWeight: 900, letterSpacing: "-0.02em", margin: "0 0 12px" }}>Application submitted!</h1>
            <p style={{ color: "#6b7280", fontSize: 15, lineHeight: 1.65, margin: "0 0 8px" }}>
              Thank you for applying to the Schedly Partner Program. We review all applications within <strong style={{ color: "#111827" }}>2 business days</strong>.
            </p>
            <p style={{ color: "#9ca3af", fontSize: 14, margin: "0 0 32px" }}>
              Updates will be sent to <strong style={{ color: "#4f46e5" }}>{formData.contactEmail}</strong>
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <Link href="/partner" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 20px", background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: "none" }}>
                ← Back to Partner Page
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── MAIN FORM ──────────────────────────────────────────────────────────────
  return (
    <>
      <Head>
        <title>Apply to Partner Program — Schedly</title>
        <meta name="description" content="Apply to become a Schedly White-Label Reseller. Your brand, your pricing — start earning recurring revenue from scheduling." />
        <meta name="robots" content="noindex" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />
        <style dangerouslySetInnerHTML={{ __html: `
          * { font-family: 'Inter', system-ui, sans-serif; box-sizing: border-box; }
          .field-input:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.12) !important; outline: none; }
          .btn-submit { position: relative; background: linear-gradient(135deg,#6366f1 0%,#4f46e5 100%); box-shadow: 0 4px 18px rgba(99,102,241,0.35); transition: all 0.25s ease; overflow: hidden; }
          .btn-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(99,102,241,0.5); }
          .btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }
          .blob { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; animation: blob-morph 8s ease-in-out infinite; }
          @keyframes blob-morph {
            0%,100% { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; }
            50% { border-radius: 50% 50% 33% 67% / 55% 27% 73% 45%; }
          }
          @media (max-width: 860px) { .apply-layout { flex-direction: column !important; } .apply-left { display: none !important; } .apply-right { max-height: none !important; overflow: auto !important; } }
        ` }} />
      </Head>

      <div style={{ minHeight: "100vh", display: "flex", fontFamily: "Inter,system-ui,sans-serif" }}>

        {/* ── LEFT PANEL ──────────────────────────────────────────────── */}
        <div className="apply-left" style={{ width: 420, flexShrink: 0, background: "linear-gradient(160deg,#0f0c29,#1a1040,#302b63)", position: "relative", overflow: "hidden", padding: "48px 40px", display: "flex", flexDirection: "column" }}>
          {/* Blobs */}
          <div className="blob" style={{ position: "absolute", top: "15%", right: "-15%", width: 280, height: 280, background: "rgba(99,102,241,0.15)", filter: "blur(60px)" }} />
          <div className="blob" style={{ position: "absolute", bottom: "20%", left: "-10%", width: 220, height: 220, background: "rgba(124,58,237,0.12)", filter: "blur(50px)", animationDelay: "-4s" }} />

          <div style={{ position: "relative", zIndex: 1 }}>
            {/* Logo */}
            <a href="https://schedly.io" style={{ display: "inline-block", marginBottom: 52 }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/schedly-logo-white.png" alt="Schedly" style={{ height: 28, opacity: 0.95 }} />
            </a>

            <div style={{ marginBottom: 8 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", background: "rgba(99,102,241,0.2)", border: "1px solid rgba(165,180,252,0.25)", borderRadius: 20, color: "#a5b4fc", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 16 }}>
                ✦ PARTNER PROGRAM
              </span>
            </div>

            <h1 style={{ color: "#fff", fontSize: 30, fontWeight: 900, letterSpacing: "-0.025em", lineHeight: 1.2, margin: "0 0 14px" }}>
              Build a scheduling business under your brand
            </h1>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 15, lineHeight: 1.65, margin: "0 0 44px" }}>
              Join the Schedly Partner Program and start reselling scheduling as your own product. Wholesale pricing, 100% white-label, full API.
            </p>

            {/* Perks */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {PERKS.map((p) => (
                <div key={p.title} style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(99,102,241,0.18)", border: "1px solid rgba(165,180,252,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 16 }}>
                    {p.emoji}
                  </div>
                  <div>
                    <div style={{ color: "#e0e7ff", fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{p.title}</div>
                    <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 12, lineHeight: 1.5 }}>{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer note */}
            <div style={{ marginTop: "auto", paddingTop: 44 }}>
              <div style={{ padding: "16px", background: "rgba(255,255,255,0.05)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ color: "#a5b4fc", fontSize: 12, fontWeight: 700, marginBottom: 4 }}>⚡ Fast approval</div>
                <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 12, lineHeight: 1.5 }}>
                  Most applications reviewed within 2 business days. API credentials sent immediately on approval.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ─────────────────────────────────────────────── */}
        <div className="apply-right" style={{ flex: 1, background: "#f8fafc", overflowY: "auto", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "48px 24px" }}>
          <div style={{ width: "100%", maxWidth: 560 }}>

            {/* Mobile logo */}
            <div style={{ display: "none" }} className="mobile-logo">
              <Link href="/partner" style={{ display: "inline-block", marginBottom: 32 }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/schedly-logo.png" alt="Schedly" style={{ height: 28 }} />
              </Link>
            </div>

            {/* Header */}
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ color: "#111827", fontSize: 26, fontWeight: 900, letterSpacing: "-0.02em", margin: "0 0 6px" }}>Partner application</h2>
              <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>Fill in the details below — takes about 3 minutes.</p>
            </div>

            {/* Form card */}
            <form onSubmit={handleSubmit}>
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 18, padding: "28px", boxShadow: "0 4px 20px rgba(0,0,0,0.04)", marginBottom: 16 }}>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>Company name <span style={{ color: "#ef4444" }}>*</span></label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => update("companyName", e.target.value)}
                      placeholder="Acme Inc."
                      className="field-input"
                      style={inputStyle}
                      required
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Your name <span style={{ color: "#ef4444" }}>*</span></label>
                    <input
                      type="text"
                      value={formData.contactName}
                      onChange={(e) => update("contactName", e.target.value)}
                      placeholder="Jane Smith"
                      className="field-input"
                      style={inputStyle}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>Work email <span style={{ color: "#ef4444" }}>*</span></label>
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => update("contactEmail", e.target.value)}
                      placeholder="jane@acme.com"
                      className="field-input"
                      style={inputStyle}
                      required
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Website</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => update("website", e.target.value)}
                      placeholder="https://acme.com"
                      className="field-input"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Expected number of clients</label>
                  <div style={{ position: "relative" }}>
                    <select
                      value={formData.estimatedClients}
                      onChange={(e) => update("estimatedClients", e.target.value)}
                      className="field-input"
                      style={{ ...inputStyle, appearance: "none", paddingRight: 36, cursor: "pointer", color: formData.estimatedClients ? "#111827" : "#9ca3af" }}>
                      <option value="" disabled>How many clients do you plan to onboard?</option>
                      <option value="5">1–5 clients (just starting)</option>
                      <option value="15">6–25 clients (Growth tier)</option>
                      <option value="35">26–50 clients (Scale tier)</option>
                      <option value="75">51–100 clients (Elite tier)</option>
                      <option value="150">100+ clients (Enterprise tier)</option>
                    </select>
                    <svg style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#9ca3af" }} width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Tell us about your use case <span style={{ color: "#ef4444" }}>*</span></label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => update("description", e.target.value)}
                    rows={4}
                    placeholder="What's your business? How do you plan to use Schedly? Who are your clients? What does success look like for you?"
                    className="field-input"
                    style={{ ...inputStyle, resize: "none", lineHeight: 1.6 }}
                    required
                  />
                </div>
              </div>

              {/* Privacy note */}
              <p style={{ color: "#9ca3af", fontSize: 12, lineHeight: 1.6, marginBottom: 16, padding: "0 4px" }}>
                By submitting, you agree to our{" "}
                <a href="/terms" style={{ color: "#6366f1", textDecoration: "none" }}>Terms of Service</a>{" "}
                and{" "}
                <a href="/privacy" style={{ color: "#6366f1", textDecoration: "none" }}>Privacy Policy</a>.
                We&apos;ll only contact you about your application.
              </p>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="btn-submit"
                style={{ width: "100%", padding: "15px", borderRadius: 12, border: "none", color: "#fff", fontSize: 16, fontWeight: 800, cursor: "pointer", letterSpacing: "-0.01em", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                {submitting ? (
                  <>
                    <svg style={{ animation: "spin 1s linear infinite" }} width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Partner Application
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </>
                )}
              </button>

              <style dangerouslySetInnerHTML={{ __html: `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }` }} />
            </form>

            <p style={{ textAlign: "center", color: "#9ca3af", fontSize: 13, marginTop: 20 }}>
              Already a partner?{" "}
              <Link href="/partner/login" style={{ color: "#4f46e5", fontWeight: 600, textDecoration: "none" }}>
                Sign in to your portal →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
